import React, { useState } from 'react';
import { AlertTriangle, X, DollarSign, Wrench, Zap, Shield } from '../lib/icons';
import ContentSlot from './ContentSlot';

const FailedRepairs = () => {
  const [selectedImage, setSelectedImage] = useState<string | null>(null);

  const failedRepairs = [
    {
      id: 1,
      title: "DIY PS5 HDMI Port",
      image: "/ps5 ripped pads.jpg",
      description: "Customer attempted to replace their PS5 HDMI port without the proper tools, using too little heat to remove the original port. In the process, several pads were ripped off the board.",
      damage: "Fortunately, this one was repairable — but it required extra time and advanced micro-soldering to rebuild the connections. What could have been a same-day repair turned into a multi-day turnaround.",
      cost: "In more severe cases, the number of damaged pads can make the repair cost so high that it's no longer worth fixing. Trace repair starting at $10 per trace.",
      quote: "The solder wouldn't melt, so I figured it was just stuck… gave it a little tug."
    },
    {
      id: 2,
      title: "PS5 Fan Connector",
      image: "/ps5 ripped fan connector 1.jpg",
      description: "Customer attempted to clean their PS5 after overheating issues and accidentally pulled up on the fan connector. This lifted the connector off the board and tore several pads in the process.",
      damage: "Because the pads were gone, we couldn't simply solder a new connector in place. We had to secure the connector to the board using a custom anchor method and run micro jumpers to reconnect each damaged trace.",
      cost: "What could have been a quick same-day cleaning turned into a multi-day micro-soldering job — a common issue we see when the fan cable is pulled instead of gently lifted from the edges. Up to $10 per ripped trace.",
      quote: "The guy on YouTube made it look so easy."
    },
    {
      id: 3,
      title: "DIY Nintendo Switch USB-C Port",
      image: "/IMG_3129.jpg",
      description: "Customer (or another shop) attempted to replace the Nintendo Switch USB-C charging port after it stopped charging or docking. Unfortunately, the USB-C port on the Switch motherboard is one of the most delicate components in console repair. When too much heat or pressure is used, the tiny pads underneath the port can easily rip off the board.",
      damage: "Severe pad and trace damage — often to the point of being unrepairable. Unlike PS5 or Xbox HDMI ports where we can run a few jumper wires to rebuild missing pads, the Switch's USB-C port has over 20 data and power lines, and once several are torn, the board becomes nearly impossible to restore reliably.",
      cost: "Repair declined due to extensive pad loss. Every case we've seen with ripped pads on a Switch USB-C port has been beyond repair — which is unfortunate for the customer, the technician, and the environment, as it often means one more console ends up in the landfill.",
      quote: "How hard could it be to replace a charging port?"
    },
    {
      id: 4,
      title: "DIY Xbox HDMI Port",
      image: "/xbox failed hdmi port replacement.jpg",
      description: "Customer attempted to replace their Xbox HDMI port after experiencing no video output. During the process, excessive heat or uneven pressure was used to remove the old port, resulting in several lifted pads and torn traces on the motherboard.",
      damage: "Multiple lifted HDMI pads requiring micro-soldering and jumper wire reconstruction. The HDMI ports on Xbox consoles are more durable than on some systems, but the pads underneath are still fragile.",
      cost: "+$60–$90 on top of the standard HDMI port repair. Once a few signal lines rip away from the board, we have to spend extra time under the microscope running precision jumpers to restore the video and clock lines. What would've been a straightforward repair turns into a complex micro-soldering project — sometimes pushing the cost high enough that replacement becomes more practical than repair.",
      quote: "I watched three YouTube videos, I thought I had it down."
    }
  ];

  const stats = [
    {
      icon: DollarSign,
      title: "Average Cost Increase",
      value: "150%",
      description: "Failed DIY repairs cost 2.5x more to fix"
    },
    {
      icon: AlertTriangle,
      title: "Success Rate",
      value: "15%",
      description: "Only 15% of DIY console repairs succeed"
    },
    {
      icon: X,
      title: "Total Loss Rate",
      value: "35%",
      description: "35% of DIY attempts result in unrepairable damage"
    }
  ];

  return (
    <section id="failed-repairs" className="py-20 bg-[#0D0D0D]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="flex items-center justify-center mb-4">
            <AlertTriangle className="h-12 w-12 text-[#FF1744] mr-4" />
            <h2 className="text-4xl font-bold text-[#E0E0E0]">
              <ContentSlot slotId="failed-heading" fallback="Failed DIY Repairs" contentType="heading" pageSection="failed-repairs" label="Section Heading" />
            </h2>
          </div>
          <ContentSlot
            slotId="failed-subtitle"
            fallback='"I got a guy that will do it for cheaper..." - Famous last words before disaster strikes. See what happens when console repairs go wrong.'
            contentType="paragraph"
            as="p"
            pageSection="failed-repairs"
            label="Section Subtitle"
            className="text-xl text-[#B0B0B0] max-w-4xl mx-auto mb-8"
          />
          
          {/* Warning Banner */}
          <div className="bg-gradient-to-r from-[#FF1744] to-orange-600 text-white rounded-2xl p-6 max-w-4xl mx-auto">
            <div className="flex items-center justify-center mb-4">
              <AlertTriangle className="h-8 w-8 mr-3" />
              <h3 className="text-2xl font-bold">
              <ContentSlot slotId="failed-warning-title" fallback="WARNING: Graphic Content" contentType="subheading" pageSection="failed-repairs" label="Warning Title" />
            </h3>
            </div>
            <ContentSlot
              slotId="failed-warning-body"
              fallback="The following images contain scenes of console carnage that may be disturbing to gamers. Viewer discretion is advised."
              contentType="paragraph"
              as="p"
              pageSection="failed-repairs"
              label="Warning Body"
              className="text-lg"
            />
          </div>
        </div>

        {/* Statistics */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
          {stats.map((stat, index) => (
            <div key={index} className="bg-gray-900 rounded-xl p-6 text-center border border-gray-800">
              <div className="bg-[#FF1744]/20 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <stat.icon className="h-8 w-8 text-[#FF1744]" />
              </div>
              <div className="text-3xl font-bold text-[#FF1744] mb-2">{stat.value}</div>
              <div className="text-lg font-semibold text-[#E0E0E0] mb-2">{stat.title}</div>
              <div className="text-[#B0B0B0] text-sm">{stat.description}</div>
            </div>
          ))}
        </div>

        {/* Failed Repairs Gallery */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16 max-w-6xl mx-auto">
          {failedRepairs.map((repair) => (
            <div key={repair.id} className="bg-gray-900 rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow duration-300 border border-gray-800">
              <div className="relative">
                <img
                  src={repair.image}
                  alt={repair.title}
                  className="w-full h-80 object-cover cursor-pointer hover:opacity-90 transition-opacity duration-200"
                  onClick={() => setSelectedImage(repair.image)}
                />
                <div className="absolute top-4 right-4 bg-[#FF1744] text-white px-3 py-1 rounded-full text-sm font-bold">
                  FAILED DIY
                </div>
              </div>

              <div className="p-6">
                <h3 className="text-2xl font-bold text-[#E0E0E0] mb-3">{repair.title}</h3>
                <p className="text-[#B0B0B0] mb-4 leading-relaxed">{repair.description}</p>

                <div className="space-y-3 mb-4">
                  <div className="bg-gray-800 p-3 rounded-lg">
                    <span className="text-[#FF1744] font-semibold block mb-1">Damage:</span>
                    <span className="text-[#E0E0E0]">{repair.damage}</span>
                  </div>
                  <div className="bg-gray-800 p-3 rounded-lg">
                    <span className="text-[#39FF14] font-semibold block mb-1">Cost:</span>
                    <span className="text-[#E0E0E0]">{repair.cost}</span>
                  </div>
                </div>

                <blockquote className="bg-gray-800 p-4 rounded-lg border-l-4 border-[#FF1744]">
                  <p className="text-[#B0B0B0] italic">{repair.quote}</p>
                  <cite className="text-[#FF1744] text-sm mt-2 block">- The Customer</cite>
                </blockquote>
              </div>
            </div>
          ))}
        </div>

        {/* Professional Service CTA */}
        <div className="bg-gradient-to-r from-[#00BFFF] to-[#39FF14] text-[#0D0D0D] rounded-2xl p-8 text-center">
          <div className="flex items-center justify-center mb-6">
            <Shield className="h-12 w-12 mr-4" />
            <h3 className="text-3xl font-bold">
              <ContentSlot slotId="failed-cta-heading" fallback="Don't Let This Be You" contentType="subheading" pageSection="failed-repairs" label="CTA Heading" />
            </h3>
          </div>
          <ContentSlot
            slotId="failed-cta-body"
            fallback="Professional repairs cost less than fixing DIY disasters. Our expert technicians have the right tools, experience, and warranty to get it done right the first time."
            contentType="paragraph"
            as="p"
            pageSection="failed-repairs"
            label="CTA Body"
            className="text-xl mb-6 max-w-3xl mx-auto"
          />
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div className="bg-white/20 rounded-lg p-4">
              <Wrench className="h-8 w-8 mx-auto mb-2" />
              <h4 className="font-bold mb-1">Professional Tools</h4>
              <p className="text-sm">Proper equipment, not Harbor Freight specials</p>
            </div>
            <div className="bg-white/20 rounded-lg p-4">
              <Zap className="h-8 w-8 mx-auto mb-2" />
              <h4 className="font-bold mb-1">Expert Technicians</h4>
              <p className="text-sm">15 years experience, not YouTube University</p>
            </div>
            <div className="bg-white/20 rounded-lg p-4">
              <Shield className="h-8 w-8 mx-auto mb-2" />
              <h4 className="font-bold mb-1">90-Day Warranty</h4>
              <p className="text-sm">Guaranteed work, not crossed fingers</p>
            </div>
          </div>
          
          <button
            onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
            className="bg-[#FF1744] text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-white hover:text-[#FF1744] transition-colors duration-200 shadow-lg"
          >
            <ContentSlot slotId="failed-cta-btn" fallback="Get Professional Repair Quote" contentType="button" pageSection="failed-repairs" label="CTA Button" />
          </button>
        </div>

        {/* Image Modal */}
        {selectedImage && (
          <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4">
            <div className="relative max-w-4xl max-h-full">
              <button
                onClick={() => setSelectedImage(null)}
                className="absolute top-4 right-4 bg-[#FF1744] text-white p-2 rounded-full hover:bg-white hover:text-[#FF1744] transition-colors duration-200 z-10"
              >
                <X className="h-6 w-6" />
              </button>
              <img
                src={selectedImage}
                alt="Failed repair detail"
                className="max-w-full max-h-full object-contain rounded-lg"
              />
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default FailedRepairs;